import React, { useState } from 'react';
import { ShieldCheck, Check, RotateCcw } from 'lucide-react';
import { formatMillions } from './forecastUsers';

const describeMacroCaseUsed = (macroCaseUsed) => {
  if (!macroCaseUsed) return null;
  if (macroCaseUsed.source === 'fallback-default') return 'Macro: fallback defaults';
  if (macroCaseUsed.source === 'central-settings') {
    return `Macro: ${macroCaseUsed.case || 'Base'} · central settings`;
  }
  return `Macro: ${macroCaseUsed.case || 'Base'}`;
};

/**
 * Persistent regional AI tools shown above every workspace stage. Keeping both
 * tools mounted preserves prompt drafts and context form state during tab and
 * stage navigation.
 */
const RegionalAiPlanningTools = ({ vm }) => {
  const {
    ForecastContextNotes,
    ForecastPromptBox,
    activeProfile,
    applyPromptChangeset,
    canEditSubmission,
    isAggregate,
    promptCurrentAnnual,
    promptLeverSensitivity,
    refinePromptLever,
    selectedMarket,
    simulatePromptChangeset,
    submission,
    submissionId,
    undoPromptChangeset,
    yearAdjustments,
    macroCase,
    macroMode,
    // Item G — FP&A Quick Fix (adjust mode)
    fpaAdjustMode,
    fpaNetEffect,
    fpaSaveState,
    saveFpaAdjustment,
    discardFpaAdjustment
  } = vm;
  const [activeTool, setActiveTool] = useState('ask');
  const [promptMetadata, setPromptMetadata] = useState(null);

  if (isAggregate) return null;

  const scope = {
    planningEntity: submissionId ? submission?.planning_entity || null : selectedMarket.planningEntity,
    division: selectedMarket.region,
    market: selectedMarket.market,
    cycleCode: submission?.cycle_code || 'FY2027_BUDGET',
    macroCase: macroCase || macroMode || 'Base'
  };

  const netExplicit = (fpaNetEffect?.effect || []).find((line) => line.field === 'netIncome');
  const macroCaseLabel = describeMacroCaseUsed(promptMetadata?.macroCaseUsed);

  return (
    <section className="forecast-panel workspace-ai-planning-tools" aria-labelledby="regional-ai-planning-title">
      {fpaAdjustMode && (
        <div className="fpa-adjust-banner" role="note">
          <div className="fpa-adjust-banner-head">
            <span className="forecast-section-code">RV_QF · FP&amp;A Quick Fix</span>
            <span className="forecast-health-chip gold">
              <ShieldCheck size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />
              FP&amp;A adjustment layer · {activeProfile.label}
            </span>
          </div>
          <p className="forecast-submit-note" style={{ marginTop: 4 }}>
            You are editing the <strong>FP&amp;A Adjusted</strong> layer for {selectedMarket.region} · {selectedMarket.market}.
            The market's submitted values stay intact. Use the Ask AI box <em>or</em> edit the OP_03 levers
            directly, then save — every change is attributed and reversible.
          </p>
          <div className="fpa-adjust-actions">
            <span className={`forecast-health-chip ${netExplicit && Math.abs(netExplicit.totalDeltaMM) >= 0.05 ? (netExplicit.totalDeltaMM >= 0 ? 'green' : 'red') : 'neutral'}`}>
              Net income vs market: {netExplicit ? `${netExplicit.totalDeltaMM >= 0 ? '+' : ''}${formatMillions(netExplicit.totalDeltaMM)}` : '—'}
            </span>
            <button
              type="button"
              className="forecast-primary-button compact"
              onClick={saveFpaAdjustment}
              disabled={!fpaNetEffect?.changed || fpaSaveState === 'saving'}
              title={!fpaNetEffect?.changed ? 'Edit a lever to create an adjustment' : 'Persist this FP&A adjustment'}
            >
              <Check size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              {fpaSaveState === 'saving' ? 'Saving…' : fpaSaveState === 'saved' ? 'Saved ✓' : 'Save FP&A adjustment'}
            </button>
            <button
              type="button"
              className="forecast-secondary-button compact"
              onClick={discardFpaAdjustment}
              disabled={!fpaNetEffect?.changed || fpaSaveState === 'saving'}
            >
              <RotateCcw size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              Discard changes
            </button>
            {fpaSaveState === 'error' && <span className="forecast-health-chip red">Save failed — retry</span>}
          </div>
        </div>
      )}
      <div className="workspace-ai-planning-head">
        <div>
          <span className="forecast-section-code">AI planning tools</span>
          <h2 id="regional-ai-planning-title">
            {fpaAdjustMode ? 'Quick Fix this package in the FP&A layer' : 'Explore alternatives after the core levers are reviewed'}
          </h2>
        </div>
        <div className="workspace-stage-tabs" role="tablist" aria-label="AI planning tools">
          <button
            type="button"
            role="tab"
            id="regional-ai-ask-tab"
            aria-selected={activeTool === 'ask'}
            aria-controls="regional-ai-ask-panel"
            className="forecast-secondary-button compact"
            onClick={() => setActiveTool('ask')}
          >
            Ask AI
          </button>
          <button
            type="button"
            role="tab"
            id="regional-ai-context-tab"
            aria-selected={activeTool === 'context'}
            aria-controls="regional-ai-context-panel"
            className="forecast-secondary-button compact"
            onClick={() => setActiveTool('context')}
          >
            Add to context store
          </button>
        </div>
        {macroCaseLabel && <span className="forecast-health-chip neutral">{macroCaseLabel}</span>}
      </div>

      <div
        id="regional-ai-ask-panel"
        role="tabpanel"
        aria-labelledby="regional-ai-ask-tab"
        hidden={activeTool !== 'ask'}
      >
        <ForecastPromptBox
          scope={scope}
          hasSubmission={Boolean(submissionId)}
          canEdit={canEditSubmission}
          disabled={Boolean(submissionId) && !canEditSubmission}
          currentLevers={yearAdjustments}
          currentAnnual={promptCurrentAnnual}
          sensitivity={promptLeverSensitivity}
          simulate={simulatePromptChangeset}
          refine={refinePromptLever}
          onApply={applyPromptChangeset}
          onUndo={undoPromptChangeset}
          onProposalMetadata={setPromptMetadata}
        />
      </div>

      <div
        id="regional-ai-context-panel"
        role="tabpanel"
        aria-labelledby="regional-ai-context-tab"
        hidden={activeTool !== 'context'}
      >
        <ForecastContextNotes
          scope={scope}
          author={activeProfile.name}
          disabled={Boolean(submissionId) && !canEditSubmission}
          title="Add planning knowledge to the context store"
          subtitle="Record durable guidance and one-time facts the AI should read before proposing forecast changes."
        />
      </div>
    </section>
  );
};

export default RegionalAiPlanningTools;
